import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Check,
  Hammer,
  Laptop,
  Settings2,
  Stethoscope,
  Store,
  Truck,
  Wrench,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { modules, calcUsedPoints } from "@/data/modules";
import { presets, getPresetPlan, type PresetDefinition } from "@/data/presets";
import { formatPoints } from "@/data/plans";

// ── Icon map per preset ───────────────────────────────────────
const INDUSTRY_META: Record<
  string,
  {
    icon: React.ElementType;
    description: string;
    useCases: string[];
    color: string;
  }
> = {
  agentur_beratung: {
    icon: Briefcase,
    description: "Perfekt für Unternehmensberater, Marketing-Agenturen und Freelancer aller Art.",
    useCases: [
      "Projektbasierte Abrechnung",
      "Angebote und Kostenvoranschläge",
      "CRM & Kundenpflege",
      "Ticket-basierter Support",
    ],
    color: "from-blue-500 to-indigo-600",
  },
  handwerk: {
    icon: Hammer,
    description: "Von Elektrikern über Installateure bis hin zu Schreinern – flexibel für alle Gewerke.",
    useCases: [
      "Aufmaß, Angebote & Abrechnung",
      "Materialverwaltung im Lager",
      "Fuhrpark & Mitarbeiterplanung",
      "DATEV-Export für Steuerberater",
    ],
    color: "from-emerald-500 to-teal-600",
  },
  handel_ecom: {
    icon: Store,
    description: "Für Einzelhändler und Onlineshops mit Lager, Einkauf und Shopanbindung.",
    useCases: [
      "Shop-Sync & Bestellimport",
      "Lager- und Bestandsverwaltung",
      "Einkauf & Lieferantenmanagement",
      "Rechnungen & Mahnwesen",
    ],
    color: "from-cyan-500 to-blue-600",
  },
  logistik: {
    icon: Truck,
    description: "Maßgeschneiderte Lösungen für Transport- und Logistikunternehmen.",
    useCases: [
      "Fuhrparkverwaltung",
      "Schichtplanung & Personalmanagement",
      "Lager & Wareneingang",
      "Rechnungen & Buchhaltung",
    ],
    color: "from-amber-500 to-orange-600",
  },
  it_dev: {
    icon: Laptop,
    description: "Für Softwareentwickler, IT-Dienstleister und Tech-Startups.",
    useCases: [
      "Sprint- & Lizenzabrechnung",
      "Wartungsverträge verwalten",
      "Ticketsystem & Projektmanagement",
      "CRM & Kundenverträge",
    ],
    color: "from-violet-500 to-purple-600",
  },
  gesundheit_therapie: {
    icon: Stethoscope,
    description: "Für Therapeuten, Coaches und Gesundheitsdienstleister.",
    useCases: [
      "Sitzungs- & Kursabrechnung",
      "Kundenkartei & Terminverwaltung",
      "Vertragsmanagement",
      "Buchungsbeleg-Versand per E-Mail",
    ],
    color: "from-rose-500 to-pink-600",
  },
};

// ── Module chip (read-only display) ──────────────────────────
function ModuleChipDisplay({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-md border border-primary/20 bg-primary/8 px-2 py-0.5 text-[11px] font-medium text-primary">
      {name}
    </span>
  );
}

// ── Industry card ─────────────────────────────────────────────
function IndustryCard({ preset }: { preset: PresetDefinition }) {
  const meta = INDUSTRY_META[preset.id];
  if (!meta) return null;

  const Icon = meta.icon;
  const presetModules = modules.filter((m) => preset.moduleIds.includes(m.id));
  const totalPoints = calcUsedPoints(preset.moduleIds);
  const recommendedPlan = getPresetPlan(preset);

  return (
    <div className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-card-hover flex flex-col">
      <div
        className={`absolute -right-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br ${meta.color} opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-20`}
      />

      <div className="relative flex flex-col flex-1 gap-0">
        <div
          className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${meta.color} shadow-lg`}
        >
          <Icon className="h-7 w-7 text-white" />
        </div>

        <h3 className="mb-2 font-display text-xl font-bold text-foreground">{preset.label}</h3>
        <p className="mb-5 text-sm text-muted-foreground leading-relaxed">{meta.description}</p>

        <ul className="mb-6 space-y-1.5">
          {meta.useCases.map((uc) => (
            <li key={uc} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-success shrink-0" />
              <span className="text-foreground">{uc}</span>
            </li>
          ))}
        </ul>

        <div className="mb-5 h-px bg-border" />

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Empfohlenes Setup
            </p>
            <span className="inline-flex items-center rounded-lg bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
              {totalPoints} Pkt · {recommendedPlan.name} ({formatPoints(recommendedPlan.pointsIncluded)})
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presetModules.map((m) => (
              <ModuleChipDisplay key={m.id} name={m.name} />
            ))}
          </div>
        </div>

        <Button
          size="sm"
          className="mt-auto w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90 group/btn"
          asChild
        >
          <Link to={`/konfigurator?preset=${preset.id}`}>
            <Settings2 className="h-4 w-4" />
            Setup im Konfigurator öffnen
            <ArrowRight className="h-4 w-4 ml-auto transition-transform group-hover/btn:translate-x-0.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────
const Industries = () => {
  return (
    <PageLayout>
      <Helmet>
        <title>Branchenlösungen – NextInvoice</title>
        <meta name="description" content="NextInvoice für Ihre Branche: Fertige Modul-Pakete für Agenturen, Handwerk, IT, Handel, Logistik und mehr." />
        <link rel="canonical" href="https://nxtinvoice.de/branchen" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nxtinvoice.de/" },
            { "@type": "ListItem", "position": 2, "name": "Branchenlösungen", "item": "https://nxtinvoice.de/branchen" }
          ]
        })}</script>
      </Helmet>
      <section className="relative overflow-hidden bg-background py-20 lg:py-28">
        <div className="absolute inset-0 gradient-hero pattern-grid" />
        <div className="absolute right-0 top-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-accent/8 blur-3xl" />

        <div className="container relative">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Branchen & Anwendungsbereiche</span>
            </div>

            <h1 className="mb-5 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl text-balance">
              Module für{" "}
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                jede Branche.
              </span>
            </h1>

            <p className="mb-10 text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Wählen Sie ein empfohlenes Setup – und passen Sie es im Konfigurator an Ihren
              genauen Workflow an.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                size="lg"
                className="group h-12 bg-accent px-8 text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90 relative overflow-hidden"
                asChild
              >
                <Link to="/konfigurator">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Konfigurator öffnen
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                </Link>
              </Button>
              <Button variant="ghost" size="lg" className="h-12 gap-1" asChild>
                <Link to="/features">
                  Alle Module ansehen <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-secondary/50 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-dots opacity-50" />
        <div className="container relative">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {presets.map((preset) => (
              <IndustryCard key={preset.id} preset={preset} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-20 lg:py-28">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="relative overflow-hidden rounded-3xl bg-primary p-8 lg:p-12">
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-foreground/5 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />

              <div className="relative flex flex-col items-center gap-6 text-center lg:flex-row lg:text-left lg:justify-between">
                <div className="max-w-xl">
                  <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-foreground/10">
                    <Wrench className="h-7 w-7 text-primary-foreground" />
                  </div>
                  <h2 className="mb-3 font-display text-2xl font-bold text-primary-foreground lg:text-3xl">
                    Sie brauchen ein Spezialmodul?
                  </h2>
                  <p className="text-primary-foreground/70 leading-relaxed">
                    Unser Baukasten deckt viele Szenarien ab – aber nicht jedes. Wenn Ihr Workflow
                    etwas Besonderes braucht, erweitern wir Ihr Setup. Sprechen Sie uns an.
                  </p>
                </div>

                <div className="flex flex-col gap-3 shrink-0">
                  <Button
                    size="lg"
                    className="h-14 bg-accent px-8 text-base font-semibold text-accent-foreground shadow-lg shadow-accent/25"
                    asChild
                  >
                    <Link to="/ueber-uns">
                      Enterprise anfragen
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                    asChild
                  >
                    <Link to="/konfigurator">Baukasten erkunden</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Industries;
